import { useEffect, useRef, useState } from "react";
import api from "src/services/api";
import LoadingButton from "../LoadingButton";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";

function readFileAsBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = () => {
      try {
        const base64 = reader.result.split(",")[1];
        resolve(base64);
      } catch (error) {
        reject(new Error("Erro ao processar arquivo"));
      }
    };

    reader.onerror = (error) => {
      reject(new Error("Erro na leitura do arquivo"));
    };

    reader.readAsDataURL(file);
  });
}

export default function TemplateConfigs() {
  const [templates, setTemplates] = useState({
    templates: [],
    selectedTemplate: null,
    newTemplateName: "",
  });
  const [variables, setVariables] = useState({
    upload: {
      fields: [],
      lists: {},
    },
    update: {
      fields: [],
      lists: {},
    },
  });
  const [file, setFile] = useState({
    upload: null,
    update: null,
  });
  const [loading, setLoading] = useState({
    analyze: false,
    upload: false,
    update: false,
    rename: false,
    delete: false,
  });

  const fileInputsRef = useRef({
    upload: null,
    update: null,
  });

  useEffect(() => {
    fetchTemplates();
  }, []);

  async function analyzeFile() {
    if (!file.upload) return;
    setLoading((prev) => ({ ...prev, analyze: true }));
    setVariables((prev) => ({ ...prev, upload: { fields: [], lists: {} } }));
    try {
      const formData = new FormData();
      formData.append("file", file.upload);

      const res = await api.post("/template/analyze", formData);
      if (res.data.schema.fields.length === 0) {
        alert("Modelo inválido: não foi possível encontrar variáveis");
        return;
      }
      setVariables((prev) => ({
        ...prev,
        upload: res.data.schema || {},
      }));
    } catch (error) {
      console.error(error);
      alert("Erro ao encontrar variáveis no documento.");
    } finally {
      setLoading((prev) => ({ ...prev, analyze: false }));
    }
  }

  async function fetchTemplates() {
    try {
      const res = await api.get("/template");
      setTemplates((prev) => ({
        ...prev,
        templates: res.data.templates || [],
      }));
    } catch (error) {
      console.error("Erro ao buscar templates:", error);
      alert("Falha ao carregar templates: " + error.message);
    }
  }

  async function uploadTemplate() {
    if (!file.upload) return;

    setLoading((prev) => ({ ...prev, upload: true }));
    try {
      const content = await readFileAsBase64(file.upload);
      const data = { name: file.upload.name.replace(".docx", ""), content };
      let token = localStorage.getItem("auth-token");
      const response = await api.post("/template", data, {
        headers: { "x-auth-token": token },
      });

      alert(response.data.message);
      fetchTemplates();

      setFile((prev) => ({ ...prev, upload: null }));
      setVariables((prev) => ({ ...prev, upload: { fields: [], lists: {} } }));
      if (fileInputsRef.current.upload) fileInputsRef.current.upload.value = "";
    } catch (error) {
      console.error("Upload falhou", error);
      alert("Erro ao enviar modelo: " + error.response.data.message);
    } finally {
      setLoading((prev) => ({ ...prev, upload: false }));
    }
  }

  async function fetchTemplateVariables(name) {
    try {
      const res = await api.get(`/template/${name}`);
      setVariables((prev) => ({
        ...prev,
        update: res.data.schema || {},
      }));
    } catch (error) {
      console.error(error);
    }
  }

  async function updateTemplate() {
    if (!file.update) return;

    setLoading((prev) => ({ ...prev, update: true }));
    setVariables((prev) => ({ ...prev, update: { fields: [], lists: {} } }));
    try {
      const content = await readFileAsBase64(file.update);
      let token = localStorage.getItem("auth-token");
      await api.put(
        `/template/${templates.selectedTemplate}`,
        { content },
        { headers: { "x-auth-token": token } }
      );
      fetchTemplates();
      setFile((prev) => ({ ...prev, update: null }));
      if (fileInputsRef.current.update) fileInputsRef.current.update.value = "";
    } catch (error) {
      console.error(error);
      alert("Erro ao atualizar template: " + error.message);
    } finally {
      setLoading((prev) => ({ ...prev, update: false }));
    }
  }

  async function renameTemplate(oldName, newName) {
    setLoading((prev) => ({ ...prev, rename: true }));
    try {
      let token = localStorage.getItem("auth-token");
      await api.patch(
        `/template/${oldName}/rename`,
        { newName },
        { headers: { "x-auth-token": token } }
      );
      setVariables((prev) => ({ ...prev, update: { fields: [], lists: {} } }));
      setTemplates((prev) => ({
        ...prev,
        selectedTemplate: null,
        newTemplateName: "",
      }));

      fetchTemplates();
    } catch (error) {
      console.error(error);
    } finally {
      setLoading((prev) => ({ ...prev, rename: false }));
    }
  }

  async function deleteTemplate(name) {
    if (!window.confirm("Tem certeza que deseja excluir este template?"))
      return;
    setLoading((prev) => ({ ...prev, delete: true }));
    try {
      let token = localStorage.getItem("auth-token");
      await api.delete(`/template/${name}`, {
        headers: { "x-auth-token": token },
      });

      setVariables((prev) => ({ ...prev, update: { fields: [], lists: {} } }));
      setTemplates((prev) => ({
        ...prev,
        selectedTemplate: null,
        newTemplateName: "",
      }));

      fetchTemplates();
    } catch (error) {
      console.error(error);
    } finally {
      setLoading((prev) => ({ ...prev, delete: false }));
    }
  }

  const showVariables = (variables) => {
    return (
      <div>
        <h2 className="text-lg my-4">As variáveis do modelo são:</h2>

        <div className="bg-gray-100 p-4 rounded-md flex flex-row gap-4 justify-start items-start w-fit">
          <div>
            {/* Campos Individuais */}
            <h3 className="text-base font-bold">Campos Individuais:</h3>
            <ul className="list-disc pl-5">
              {variables.fields?.map((field, index) => (
                <li key={index}>{field}</li>
              ))}
            </ul>
          </div>

          <div>
            {/* Listas */}
            <h3 className="text-base font-bold">Listas:</h3>
            {variables.lists &&
              Object.entries(variables.lists).map(([listName, listData]) => (
                <div key={listName} className="mt-2">
                  <h4 className="font-semibold">Lista {listName}:</h4>
                  <ul className="list-disc pl-5">
                    {listData.fields.map((field, index) => (
                      <li className="text-base" key={index}>
                        {field}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="flex flex-col gap-4 ml-5 mt-3">
      <h1 className="text-xl">Configurações de Modelo</h1>

      <div className="flex flex-row justify-between items-start gap-20">
        {/* Upload de Modelo */}
        <div>
          <h2 className="text-lg mb-4">Upload de Modelo</h2>
          <div className="flex gap-4">
            <input
              type="file"
              ref={(ref) => (fileInputsRef.current.upload = ref)}
              onChange={(e) => {
                setFile((prev) => ({ ...prev, upload: e.target.files[0] }));
              }}
            />
            <LoadingButton onClick={analyzeFile} loading={loading.analyze}>
              Analisar Modelo
            </LoadingButton>
          </div>

          {variables.upload.fields?.length > 0 && (
            <>
              {showVariables(variables.upload)}
              <div className="flex gap-4 my-4">
                <LoadingButton
                  onClick={() => uploadTemplate(file)}
                  loading={loading.upload}
                >
                  Enviar
                </LoadingButton>
              </div>
            </>
          )}
        </div>

        {/* Atualizar Modelo */}
        <div>
          <h2 className="text-lg mb-4">Atualizar Modelo</h2>
          <Select
            value={templates.selectedTemplate || undefined}
            onValueChange={(value) => {
              setTemplates((prev) => ({ ...prev, selectedTemplate: value }));
              fetchTemplateVariables(value);
            }}
          >
            <SelectTrigger>
              <SelectValue placeholder="Selecione um modelo" />
            </SelectTrigger>
            <SelectContent>
              <SelectGroup>
                {templates.templates.map((template) => (
                  <SelectItem key={template.name} value={template.name}>
                    {template.name}
                  </SelectItem>
                ))}
              </SelectGroup>
            </SelectContent>
          </Select>

          {variables.update.fields?.length > 0 &&
            showVariables(variables.update)}

          {templates.selectedTemplate && (
            <div className="grid gap-4 my-4 place-items-center">
              <div className="grid grid-cols-[1fr_auto] gap-4 w-full">
                <input
                  type="file"
                  ref={(ref) => (fileInputsRef.current.update = ref)}
                  className="w-64 overflow-wrap"
                  onChange={(e) => {
                    setFile((prev) => ({ ...prev, update: e.target.files[0] }));
                  }}
                />
                <LoadingButton
                  className="w-24"
                  onClick={updateTemplate}
                  loading={loading.update}
                >
                  Atualizar
                </LoadingButton>
              </div>
              <div className="grid grid-cols-[1fr_auto] gap-4 w-full">
                <input
                  type="text"
                  placeholder="Novo nome"
                  value={templates.newTemplateName}
                  className="w-full"
                  onChange={(e) =>
                    setTemplates((prev) => ({
                      ...prev,
                      newTemplateName: e.target.value.replace(/\s+/g, "_"),
                    }))
                  }
                />
                <LoadingButton
                  className="w-24"
                  onClick={() =>
                    renameTemplate(
                      templates.selectedTemplate,
                      templates.newTemplateName
                    )
                  }
                  loading={loading.rename}
                >
                  Renomear
                </LoadingButton>
              </div>
              <div className="justify-self-end">
                <LoadingButton
                  className="w-24"
                  onClick={() => deleteTemplate(templates.selectedTemplate)}
                  loading={loading.delete}
                >
                  Excluir
                </LoadingButton>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
